import { useState } from "react";
import { Flex } from "styles/utility-components";

import {
  DATA_TYPE_TO_CREATE_VERSION,
  DATA_TYPE_TO_EVENT_VERSION,
  DATA_TYPE_TO_REQUEST_VERSION,
  useRecentViews,
  viewActionTooltip,
} from "components/saved_searches/utils";
import Button from "components/ui/button/Button";
import { DropdownMenu, DropdownMenuItem, DropdownMenuTrigger } from "components/ui/dropdown_menu";
import Popover from "components/ui/popover/Popover";
import toast from "components/ui/toast/Toast";
import SavedViewsMenu from "components/views/SavedViewsMenu";
import { SaveViewModal } from "components/views/SaveViewModal";
import { useSavedViewAlert } from "lib/hooks/api/lead_subscriptions/useSavedViewAlert";
import { usePinView } from "lib/hooks/api/notices/views/usePinView";
import { useDeletePinnedView } from "lib/hooks/api/pinned_views/useDeletePinnedView";
import { useCreateSavedView } from "lib/hooks/api/saved_views/useCreateSavedView";
import { useSavedViewsDataType } from "lib/hooks/api/saved_views/useSavedViews";
import { useUpdateSavedView } from "lib/hooks/api/saved_views/useUpdateSavedView";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { createEditAlertsModalOpened } from "lib/savedViewTracking";
import { EventNames, useTracking } from "lib/tracking";
import {
  CreateSavedViewAlertFrequency,
  SavedViewSortBy,
  SavedViewSortOrder,
} from "lib/types/graphQLEnums";
import { SavedViewDataTypeUnion, SavedViewUnion } from "lib/types/savedViews";
import { assert } from "lib/utils";
import { DeleteViewModal } from "./DeleteViewModal";
import { DuplicateViewModal } from "./DuplicateViewModal";
import { EditAlertsModal } from "./EditAlertsModal";
import { EditViewModal } from "./EditViewModal";

type SearchActionsProps = {
  dataType: SavedViewDataTypeUnion;
  selectedView?: SavedViewUnion;
  setSelectedView: (viewId?: string, viewName?: string, context?: string) => void;
  currentView: {
    areSettingsEqualToDefault: boolean;
    haveSettingsChanged: boolean;
    view: SavedViewUnion["view"];
  };
  /** When true, shows a Get alerts shortcut (Notices V2). */
  showGetAlertsShortcut?: boolean;
};

function SearchActions({
  dataType,
  selectedView,
  setSelectedView,
  currentView,
  showGetAlertsShortcut = false,
}: SearchActionsProps) {
  const user = useAssertCurrentUser();
  const { logEvent } = useTracking();
  const ownsCurrentView = selectedView?.owner?.guid === user.guid;

  const { alert: savedViewAlert } = useSavedViewAlert(selectedView?.id);
  const hasAlertsEnabled = Boolean(selectedView?.id && savedViewAlert);

  const { recentViews } = useRecentViews(DATA_TYPE_TO_REQUEST_VERSION[dataType]);

  const { data: pinnedSavedViews } = useSavedViewsDataType(
    DATA_TYPE_TO_REQUEST_VERSION[dataType],
    { pinned: true, orderBy: SavedViewSortBy.PinnedRank, order: SavedViewSortOrder.Asc },
    { page: 1, pageSize: 50 },
  );
  const pinnedViews = pinnedSavedViews?.results ?? [];
  const isPinned = !!(selectedView && pinnedViews?.some((view) => view.id === selectedView.id));

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);
  const [isEditAlertsModalOpen, setIsEditAlertsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { mutate: createSavedView, isLoading: isLoadingCreate } = useCreateSavedView({
    onSuccess: async (data, vars) => {
      setSelectedView(data.createSavedView.id, vars.request.name ?? undefined, "Created search");
      setIsCreateModalOpen(false);

      toast.success(`${vars.request.name} created successfully!`);
    },
    onError: async (_data) => {
      toast.error("Failed to create search");
    },
  });

  const { mutate: updateSavedView } = useUpdateSavedView("Search actions", {
    onSuccess: async (_data, vars) => {
      setSelectedView(vars.request.id, vars.request.name ?? undefined, "Updated search");

      toast.success(`${vars.request.name} updated successfully!`);
    },
    onError: async (_data) => {
      toast.error("Failed to update search");
    },
  });

  const { mutate: unpinViewMutation } = useDeletePinnedView({
    onSuccess: async (_data, _vars) => {
      toast.success("Search unpinned successfully!");
    },
    onError: async (_data) => {
      toast.error("Failed to unpin search");
    },
  });

  const { mutate: pinView } = usePinView({
    onSuccess: async (_data, _vars) => {
      toast.success("Search pinned successfully!");
    },
    onError: async (_data) => {
      toast.error("Failed to pin search");
    },
  });

  return (
    <Flex gap="8px" justifyContent="right">
      <Popover trigger={<DropdownMenuTrigger>Saved searches</DropdownMenuTrigger>} hideArrow>
        <SavedViewsMenu
          dataType={dataType}
          pinnedViews={pinnedViews}
          recentViews={recentViews}
          setSelectedView={setSelectedView}
        />
      </Popover>

      {showGetAlertsShortcut && (
        <Button
          buttonType="secondary"
          icon="bell01"
          iconProps={
            hasAlertsEnabled
              ? { fill: "sysPrimaryDefault", color: "sysPrimaryDefault" }
              : { fill: "transparent" }
          }
          disabled={!selectedView && currentView.areSettingsEqualToDefault}
          onClick={() => {
            if (selectedView) {
              logEvent(
                EventNames.alertModalOpened,
                createEditAlertsModalOpened(
                  selectedView.name,
                  DATA_TYPE_TO_EVENT_VERSION[dataType],
                  selectedView.id,
                ),
              );
              setIsEditAlertsModalOpen(true);
            } else {
              setIsCreateModalOpen(true);
            }
          }}
        >
          {hasAlertsEnabled ? "Edit alerts" : "Get alerts"}
        </Button>
      )}

      <DropdownMenu
        trigger={
          <DropdownMenuTrigger
            buttonType="primary"
            disabled={currentView.areSettingsEqualToDefault}
          >
            Save
          </DropdownMenuTrigger>
        }
        width="fit-content"
        modal={false}
      >
        <DropdownMenuItem
          key="update-current-view"
          icon="save01"
          disabled={!selectedView || !currentView.haveSettingsChanged || !ownsCurrentView}
          loading={false}
          onClick={() => {
            assert(selectedView);
            updateSavedView({
              request: {
                id: selectedView.id,
                name: selectedView.name,
                description: selectedView.description ?? "",
                view: JSON.stringify(currentView.view),
              },
            });
          }}
        >
          Save changes
        </DropdownMenuItem>
        <DropdownMenuItem
          key="save-as-new-view"
          icon="save01"
          disabled={currentView.areSettingsEqualToDefault}
          loading={false}
          onClick={() => {
            setIsCreateModalOpen(true);
          }}
        >
          Save as new search
        </DropdownMenuItem>
      </DropdownMenu>

      <DropdownMenu
        trigger={
          <DropdownMenuTrigger
            buttonType="secondary"
            hideArrow
            icon="dotsHorizontal"
            disabled={!selectedView}
            tooltip={
              !selectedView && !currentView.areSettingsEqualToDefault
                ? { content: "Save your search to enable more actions" }
                : undefined
            }
          />
        }
        width="fit-content"
        modal={false}
      >
        <DropdownMenuItem
          key="set-alerts"
          icon="bell01"
          iconProps={{
            fill: hasAlertsEnabled ? "sysTextDefault" : "transparent",
          }}
          onClick={() => {
            assert(selectedView);
            logEvent(
              EventNames.alertModalOpened,
              createEditAlertsModalOpened(
                selectedView.name,
                DATA_TYPE_TO_EVENT_VERSION[dataType],
                selectedView.id,
              ),
            );
            setIsEditAlertsModalOpen(true);
          }}
          disabled={!selectedView}
        >
          Set alerts
        </DropdownMenuItem>
        <DropdownMenuItem
          key="unpin"
          icon="pin02"
          iconProps={{
            fill: isPinned ? "currentColor" : "transparent",
          }}
          onClick={() => {
            assert(selectedView);
            isPinned
              ? unpinViewMutation({ input: { savedViewId: selectedView.id } })
              : pinView({ id: selectedView.id, name: selectedView.name });
          }}
          disabled={!selectedView}
        >
          {isPinned ? "Unpin search" : "Pin search"}
        </DropdownMenuItem>

        <DropdownMenuItem
          key="duplicate"
          icon="copy07"
          onClick={() => setIsDuplicateModalOpen(true)}
          disabled={!selectedView}
        >
          Duplicate search
        </DropdownMenuItem>
        <DropdownMenuItem
          key="edit"
          icon="edit05"
          onClick={() => setIsEditModalOpen(true)}
          disabled={!selectedView || !ownsCurrentView}
          tooltip={viewActionTooltip("edit", selectedView, user.guid)}
        >
          Edit search
        </DropdownMenuItem>
        <DropdownMenuItem
          key="delete"
          icon="trash01"
          onClick={() => setIsDeleteModalOpen(true)}
          disabled={!selectedView || !ownsCurrentView}
          tooltip={viewActionTooltip("delete", selectedView, user.guid)}
        >
          Delete search
        </DropdownMenuItem>
      </DropdownMenu>

      <SaveViewModal
        loading={isLoadingCreate}
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        saveView={({ viewName, viewDescription, alertFrequency }) => {
          createSavedView({
            request: {
              name: viewName,
              description: viewDescription,
              dataType: DATA_TYPE_TO_CREATE_VERSION[dataType],
              view: JSON.stringify(currentView.view),
              alertFrequency:
                alertFrequency === "NEVER"
                  ? undefined
                  : (alertFrequency as CreateSavedViewAlertFrequency),
            },
          });
        }}
      />
      {selectedView && (
        <>
          <EditAlertsModal
            isOpen={isEditAlertsModalOpen}
            selectedView={selectedView}
            onOpenChange={setIsEditAlertsModalOpen}
          />
          <DuplicateViewModal
            isOpen={isDuplicateModalOpen}
            selectedView={selectedView}
            onOpenChange={(open, newViewId) => {
              setIsDuplicateModalOpen(open);
              if (!open) {
                setSelectedView(newViewId, undefined, "Duplicated search");
              }
            }}
          />
          <EditViewModal
            isOpen={isEditModalOpen}
            selectedView={selectedView}
            onOpenChange={setIsEditModalOpen}
          />
          <DeleteViewModal
            isOpen={isDeleteModalOpen}
            selectedView={selectedView}
            onOpenChange={setIsDeleteModalOpen}
          />
        </>
      )}
    </Flex>
  );
}

export default SearchActions;
